import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Landing } from 'pages';
import { Navbar } from 'components';
import LoadingDialog from 'components/Loader';
import { useAccount, useAccountEffect, useDisconnect, useSwitchChain } from 'wagmi';
import { networkConfig } from 'config';
import '../src/styles/globals.css';
import TermsAndConditionModal from 'pages/TermsAndConditionModal';
import { TermsAndCondition } from 'pages/TermsAndCondition';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { QuantMode } from 'pages/Chat/QuantMode';
import { authHelperActions } from 'store/auth/helper-actions';
import { authActions, INITIAL_AUTH_NONCE_RESPONSE, INITIAL_LOGIN_RESPONSE } from 'store/auth';
import { AUTH_TOKEN, MOBILE_BREAK_POINTS } from 'data/constant';
import { userHelperActions } from 'store/user/helper-actions';
import { SidebarButton } from 'components/Navbar/Sidebar';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';

const ConnectButtonWrap = styled('div')`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0; //16px;

	@media (max-width: ${MOBILE_BREAK_POINTS}px) {
		display: none;
	}
`;

const DApp: React.FC = () => {
	const { switchChain } = useSwitchChain();
	const { isConnected, chain, address } = useAccount();
	const dispatch = useDispatch();
	const { disconnect } = useDisconnect();
	const authState = useSelector((state: RootState) => state.auth);
	const savedAddress = authState.loginResponse.address ?? '';
	const normalisedAddress = address ?? '';

	React.useEffect(() => {
		if (isConnected && chain === undefined) {
			switchChain({ chainId: networkConfig.HypeEvm.chainId });
		}
	}, [chain, isConnected, switchChain]);

	const onDisconnect = useCallback(() => {
		dispatch(authActions.setAuthMessage(INITIAL_AUTH_NONCE_RESPONSE));
		localStorage.removeItem('token');
		localStorage.removeItem('appTId');
		dispatch(authActions.setLogin(INITIAL_LOGIN_RESPONSE));
		disconnect();
	}, [disconnect, dispatch]);

	React.useEffect(() => {
		if (!authState.isAppReady && authState.isRestoring) {
			dispatch(authHelperActions.hydrate());
		}
	}, [authState.isAppReady, authState.isRestoring, dispatch]);

	useAccountEffect({
		onDisconnect() {
			onDisconnect();
		},
	});

	React.useEffect(() => {
		if (savedAddress.toLowerCase() !== normalisedAddress?.toLowerCase()) {
			const item = {
				address: isConnected ? normalisedAddress : '',
				token: AUTH_TOKEN,
			};
			dispatch(authActions.setLogin(item));
		}
	}, [dispatch, isConnected, normalisedAddress, savedAddress]);

	React.useEffect(() => {
		if (
			address &&
			authState.loginResponse.address !== '' &&
			address.toLowerCase() !==
				authState.loginResponse.address.toLowerCase()
		) {
			onDisconnect();
		}
	}, [address, authState.loginResponse.address, onDisconnect]);

	const fetchUserDetails = useCallback(() => {
		if (address) {
			dispatch(
				userHelperActions.hydrateUser({
					wallet_address: address as string,
				}),
			);
		}
	}, [address, dispatch]);

	useEffect(() => {
		fetchUserDetails();
	}, [address, fetchUserDetails]);

	const quantModeContent = (
		<Box sx={{ display: 'flex', width: '100%' }}>
			<Box
				sx={{
					flexGrow: 1,
					display: 'flex',
					flexDirection: 'column',
					position: 'relative',
				}}
			>
				<Navbar />
				<QuantMode />
			</Box>
			<ConnectButtonWrap>
				<SidebarButton />
			</ConnectButtonWrap>
		</Box>
	);

	const vaultModeContent = (
		<>
			<Navbar />
			<Landing />
		</>
	);

	return (
		<Router>
			<div>
				<LoadingDialog />

				{window.location?.pathname !== '/terms-and-condition' && (
					<TermsAndConditionModal />
				)}

				<Routes>
					<Route path="/quant-mode" element={quantModeContent} />
					<Route path="/vault-mode" element={vaultModeContent} />
					<Route
						path="/terms-and-condition"
						element={<TermsAndCondition />}
					/>
					<Route path="/" element={quantModeContent} />
				</Routes>
			</div>
		</Router>
	);
};

export default DApp;
